import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Robot, ChatCircle } from "@phosphor-icons/react";
import { debtorApi, bankApi } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import DebtorHeader from "@/components/debtor/DebtorHeader";
import MessageGenerator from "@/components/debtor/MessageGenerator";
import ReplyAnalyzer from "@/components/debtor/ReplyAnalyzer";
import PaymentModal from "@/components/debtor/PaymentModal";
import CronogramaCuotas from "@/components/debtor/CronogramaCuotas";

export default function DebtorDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [debtor, setDebtor] = useState(null);
  const [bank, setBank] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, b] = await Promise.all([
        debtorApi.get(id),
        bankApi.get().catch(() => null),
      ]);
      setDebtor(d);
      setBank(b);
    } catch {
      toast.error("No se encontró el deudor");
      nav("/deudores");
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const handleReclassify = async () => {
    setClassifying(true);
    try {
      const r = await debtorApi.classify(id);
      toast.success(`Reclasificado: ${r.risk}`);
      load();
    } catch {
      toast.error("Error reclasificando");
    } finally {
      setClassifying(false);
    }
  };

  if (!debtor) return <div className="text-muted-foreground">Cargando deudor…</div>;

  return (
    <div className="space-y-6" data-testid="debtor-detail-page">
      <Link
        to="/deudores"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={16} /> Volver a cartera
      </Link>

      <ErrorBoundary section="Encabezado del deudor">
        <DebtorHeader
          debtor={debtor}
          onReclassify={handleReclassify}
          classifying={classifying}
          onAbono={() => setPayOpen(true)}
        />
      </ErrorBoundary>

      {(debtor.cronograma_pagos || []).length > 0 && (
        <ErrorBoundary section="Cronograma de cuotas">
          <CronogramaCuotas debtor={debtor} onAfterChange={load} />
        </ErrorBoundary>
      )}

      <Tabs defaultValue="mensaje" className="space-y-6">
        <TabsList data-testid="detail-tabs">
          <TabsTrigger value="mensaje" data-testid="tab-mensaje">
            <Robot size={16} weight="duotone" className="mr-2" /> Mensaje IA
          </TabsTrigger>
          <TabsTrigger value="respuesta" data-testid="tab-respuesta">
            <ChatCircle size={16} weight="duotone" className="mr-2" /> Respuesta
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mensaje" className="space-y-4">
          <ErrorBoundary section="Generador de mensajes">
            <MessageGenerator
              debtor={debtor}
              bank={bank}
              initialMessage={debtor.last_message || ""}
              onAfterAction={load}
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="respuesta" className="space-y-4">
          <ErrorBoundary section="Analizador de respuestas">
            <ReplyAnalyzer debtor={debtor} onAfterAnalyze={load} />
          </ErrorBoundary>
        </TabsContent>
      </Tabs>

      <PaymentModal
        open={payOpen}
        onOpenChange={setPayOpen}
        debtor={debtor}
        onDone={() => load()}
      />
    </div>
  );
}
